<template>
  <div class="evaluate-settings p-base">
    
    <!-- Header with Save Button -->
    <div class="flex items-start justify-between mb-8">
      <div>
        <h3 class="text-[28px] leading-tight font-semibold text-[#2c3e50] mb-2">Evaluation Library</h3>
        <p class="text-[#666] text-base">
          Define the master list of performance metrics for your organization. <br>
          Weights will be assigned dynamically when configuring individual Roles.
        </p>
      </div>
      <vs-button 
        @click="saveFactorsToServer" 
        color="success" 
        type="filled" 
        :disabled="!isDirty"
        class="px-8 py-2.5 shadow-md hover:shadow-lg transform active:scale-95 transition-all duration-200"
      >
        <span class="font-bold text-white text-base">Save Changes</span>
      </vs-button>
    </div>

    <!-- Main Content Area: Factors List -->
    <vs-row>
      <vs-col class="w-full">
        <div class="bg-white rounded-lg p-6 shadow-sm border border-gray-100 mt-2">
          
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-sm font-bold text-left text-[#2c3e50] uppercase tracking-wider m-0">
              Master Criteria
            </h3>
          </div>

          <div class="space-y-4">
            <div v-if="factors.length === 0" class="text-center py-8 text-gray-400 italic">
              No criteria defined yet. Click below to add one.
            </div>

            <!-- List of Criteria -->
            <transition-group name="list" tag="div" class="space-y-4">
              <div 
                v-for="(criterion, index) in factors" 
                :key="criterion._id" 
                class="criterion-item border-b border-gray-100 last:border-b-0 py-3"
              >
                <vs-row vs-align="center" vs-type="flex" vs-justify="space-between" class="w-full">
                  
                  <!-- Left Column: Title & Description -->
                  <vs-col vs-w="9">
                    <div class="text-left pr-4">
                      <div class="flex items-baseline gap-2 mb-1">
                        <h4 class="font-medium text-base text-[#2c3e50]">{{ criterion.name }}</h4>
                      </div>
                      <p v-if="criterion.desc" class="text-sm text-gray-500 italic mt-1 leading-relaxed">
                        {{ criterion.desc }}
                      </p>
                    </div>
                  </vs-col>

                  <!-- Right Column: Controls -->
                  <vs-col vs-w="3">
                    <div class="flex items-center justify-end gap-2 h-full">
                      <vx-tooltip text="Edit Criteria" position="top">
                        <vs-button 
                          type="flat" 
                          color="primary" 
                          icon-pack="feather" 
                          icon="icon-edit-2" 
                          class="w-8 h-8 rounded-lg hover:bg-primary-light ml-2" 
                          @click="openCriterionModal(index)" 
                        />
                      </vx-tooltip>
                      
                      <vx-tooltip text="Delete Criteria" position="top">
                        <vs-button 
                          type="flat" 
                          color="danger" 
                          icon="delete" 
                          class="w-8 h-8 rounded-lg hover:bg-danger-light" 
                          @click="removeCriterion(index)" 
                        />
                      </vx-tooltip>
                    </div>
                  </vs-col>
                </vs-row>
              </div>
            </transition-group>

            <!-- Add Button (Using your custom yellow style) -->
            <vs-row class="mt-8 mb-4">
              <vs-col class="w-full flex items-center justify-center">
                <vs-button type="border" class="custom-category-btn" @click="openCriterionModal(-1)">
                  <!-- Wrap contents in a flex container to align icon and text perfectly -->
                  <div class="flex items-center justify-center">
                    <i class="material-icons text-[20px]">add</i>
                    <span class="ml-1 font-medium">Add New Criteria</span>
                  </div>
                </vs-button>
              </vs-col>
            </vs-row>

          </div>
        </div>
      </vs-col>
    </vs-row>

    <!-- Modals -->
    <vs-popup 
      class="criteria-popup" 
      :title="isEditing ? 'Edit Criteria' : 'Add New Criteria'" 
      :active.sync="showCriteriaModal"
    >
      <div class="p-2 mt-2">
        <vs-input 
          v-model="newCriteria.name" 
          label="Criteria Name" 
          placeholder="e.g. Problem Solving" 
          class="w-full mb-6 font-medium" 
        />
        
        <vs-textarea 
          v-model="newCriteria.desc" 
          label="Description (Guidance for Evaluators)" 
          placeholder="Describe what this metric evaluates..." 
          class="w-full mb-6 text-sm" 
          rows="3"
        />
        
        <div class="flex justify-end gap-3 mt-4">
          <vs-button type="border" color="dark" @click="closeCriterionModal">Cancel</vs-button>
          <vs-button color="primary" @click="saveCriterion">
            {{ isEditing ? 'Save Changes' : 'Add Criteria' }}
          </vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- Unsaved Changes Popup -->
    <vs-popup class="criteria-popup" title="Unsaved Changes" :active.sync="showUnsavedChangesPopup">
      <div class="p-2 text-left mt-2">
        <div class="bg-[#FFF7E6] rounded-lg p-4 flex items-start gap-3 mb-6">
          <div class="text-[#FF9F43] mt-0.5">
            <svg class="w-[20px] h-[20px]" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
            </svg>
          </div>
          <div>
            <h4 class="text-[#FF9F43] font-bold text-[14px] mb-1">Unsaved modifications</h4>
            <p class="text-[#FF9F43] text-[13px] font-medium">You have metric changes that are currently unsaved.</p>
          </div>
        </div>
        <p class="text-[#666] text-[14px] mb-4 leading-relaxed">
          Leaving this tab will discard all recent additions or edits made to the evaluation metrics.
        </p>
        <p class="text-[#2c3e50] font-bold text-[14px] mb-8">
          Do you want to save these changes before leaving?
        </p>

        <div class="flex items-center justify-end gap-3">
          <vs-button type="border" color="dark" class="px-5 py-2 text-sm font-medium" @click="handleDiscardAndLeave">Discard</vs-button>
          <vs-button color="success" type="filled" class="px-6 py-2 text-sm font-bold text-white shadow-md hover:shadow-lg transition-all" @click="handleSaveAndLeave">Save & Leave</vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- Unsaved Changes Popup -->
    <vs-popup class="popup-card" :active.sync="showUnsavedChangesPopup" :button-close-hidden="true">
      <div class="flex justify-between items-center px-6 py-4 border-b border-gray-100">
        <h3 class="text-[16px] font-bold text-[#2c3e50]">Unsaved Changes</h3>
        <button @click="closeUnsavedPopup" class="text-gray-400 hover:text-gray-600 transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>
      </div>

      <div class="p-6 text-left">
        <div class="bg-[#FFF7E6] rounded-lg p-4 flex items-start gap-3 mb-6">
          <div class="text-[#FF9F43] mt-0.5">
            <svg class="w-[20px] h-[20px]" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
            </svg>
          </div>
          <div>
            <h4 class="text-[#FF9F43] font-bold text-[14px] mb-1">Unsaved modifications</h4>
            <p class="text-[#FF9F43] text-[13px] font-medium">You have metric changes that are currently unsaved.</p>
          </div>
        </div>
        <p class="text-[#666] text-[14px] mb-4 leading-relaxed">
          Leaving this tab will discard all recent additions or edits made to the evaluation metrics.
        </p>
        <p class="text-[#2c3e50] font-bold text-[14px] mb-8">
          Do you want to save these changes before leaving?
        </p>

        <div class="flex items-center justify-end gap-3">
          <vs-button type="border" color="dark" class="px-5 py-2 text-sm font-medium" @click="handleDiscardAndLeave">Discard</vs-button>
          <vs-button color="success" type="filled" class="px-6 py-2 text-sm font-bold text-white shadow-md hover:shadow-lg transition-all" @click="handleSaveAndLeave">Save & Leave</vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash';
import axios from 'axios'; 

export default {
  name: "OrgSettingsCriteriaLibrary",
  data() {
    return {
      factors: [],
      originalFactorsStr: "",
      
      showCriteriaModal: false,
      isEditing: false,
      editingIndex: -1,
      newCriteria: { name: '', desc: '' },

      // Unsaved changes tracking
      isDirty: false,
      showUnsavedChangesPopup: false,
      pendingTab: null,
      resolveNavigation: null,
      routerGuard: null,
    };
  },
  computed: {
    orgData() {
      return this.$store.state.organization.orgData;
    }
  },
  watch: {
    factors: {
      deep: true,
      handler(newVal) {
        if (!this.originalFactorsStr) return;
        // Strip _id before comparing so internal Vue IDs don't trigger dirty state
        const cleanNew = newVal.map(({ _id, ...rest }) => rest);
        this.isDirty = JSON.stringify(cleanNew) !== this.originalFactorsStr;
        this.$emit('dirty-state-changed', this.isDirty);
      }
    }
  },
  mounted() {
    if (this.orgData && this.orgData._id) {
      this.fetchFactors();
    }

    // Router guards for unsaved changes
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    if (this.$router) {
      this.routerGuard = this.$router.beforeEach((to, from, next) => {
        if (this.isDirty) {
          this.resolveNavigation = next;
          this.showUnsavedChangesPopup = true;
        } else {
          next();
        }
      });
    }
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    if (this.routerGuard) this.routerGuard();
  },
  methods: {
    generateId() {
      return 'crit_' + Math.random().toString(36).substr(2, 9);
    },
    
    // --- API Calls ---
    async fetchFactors() {
      this.$vs.loading();
      try {
        const response = await axios.get(`http://127.0.0.1:8000/api/v2/organization/${this.orgData._id}/evaluation-factors`);
        if (response.data && response.data.data) {
          this.factors = response.data.data.map(f => ({
            ...f,
            _id: this.generateId() // For Vue transitions
          }));
        } else {
          this.factors = [];
        }
        
        // Save clean string for dirty checking
        const cleanFactors = this.factors.map(({ _id, ...rest }) => rest);
        this.originalFactorsStr = JSON.stringify(cleanFactors);
        this.isDirty = false;
        
      } catch (error) {
        console.error("Failed to fetch evaluation factors:", error);
        this.$vs.notify({ title: 'Error', text: 'Could not load evaluation metrics.', color: 'danger' });
      } finally {
        this.$vs.loading.close();
      }
    },

    async saveFactorsToServer() {
      if (this.factors.some(f => !f.name.trim())) {
        this.$vs.notify({ title: 'Validation Error', text: 'All criteria must have a name.', color: 'warning' });
        return false;
      }

      this.$vs.loading();
      try {
        const cleanFactors = this.factors.map(({ _id, ...rest }) => rest);
        await axios.post(`http://127.0.0.1:8000/api/v2/organization/${this.orgData._id}/evaluation-factors`, {
          factors: cleanFactors
        });
        
        this.originalFactorsStr = JSON.stringify(cleanFactors);
        this.isDirty = false;
        this.$emit('dirty-state-changed', false);
        this.$vs.notify({ title: 'Success', text: 'Evaluation library saved successfully.', color: 'success' });
        return true;
      } catch (error) {
        console.error("Failed to save evaluation factors:", error);
        this.$vs.notify({ title: 'Error', text: 'Unable to save metrics. Please try again.', color: 'danger' });
        return false;
      } finally {
        this.$vs.loading.close();
      }
    },

    // --- Modal Logic ---
    openCriterionModal(index) {
      if (index === -1) {
        // Add Mode
        this.isEditing = false;
        this.editingIndex = -1;
        this.newCriteria = { name: '', desc: '' };
      } else {
        // Edit Mode
        this.isEditing = true;
        this.editingIndex = index;
        this.newCriteria = cloneDeep(this.factors[index]);
      }
      this.showCriteriaModal = true;
    },

    closeCriterionModal() {
      this.showCriteriaModal = false;
      this.newCriteria = { name: '', desc: '' };
    },

    saveCriterion() {
      if (!this.newCriteria.name.trim()) {
        this.$vs.notify({ title: 'Required', text: 'Criteria Name is required', color: 'warning' });
        return;
      }

      if (this.isEditing) {
        this.factors.splice(this.editingIndex, 1, { ...this.newCriteria });
      } else {
        this.factors.push({
          ...this.newCriteria,
          _id: this.generateId()
        });
      }
      this.closeCriterionModal();
    },

    removeCriterion(index) {
      const criterionName = this.factors[index].name;
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirm Deletion',
        text: `Are you sure you want to remove "${criterionName}" from your library?`,
        acceptText: 'Remove',
        cancelText: 'Cancel',
        accept: () => {
          this.factors.splice(index, 1);
          this.$vs.notify({ title: 'Removed', text: 'Criteria removed from library.', color: 'success', position: 'top-right' });
        }
      });
    },

    // --- Unsaved Changes Logic ---
    handleBeforeUnload(event) {
      if (!this.isDirty) return;
      event.preventDefault();
      event.returnValue = ''; 
    },
    triggerUnsavedPopup(targetTab) {
      this.pendingTab = targetTab;
      this.showUnsavedChangesPopup = true;
    },
    closeUnsavedPopup() {
      this.showUnsavedChangesPopup = false;
      this.pendingTab = null;
      if (this.resolveNavigation) {
        this.resolveNavigation(false);
        this.resolveNavigation = null;
      }
    },
    async handleSaveAndLeave() {
      const saved = await this.saveFactorsToServer();
      if (saved) {
        this.showUnsavedChangesPopup = false;
        if (this.pendingTab) {
          this.$emit('proceed-to-tab', this.pendingTab);
          this.pendingTab = null;
        }
        if (this.resolveNavigation) {
          this.resolveNavigation();
          this.resolveNavigation = null;
        }
      }
    },
    handleDiscardAndLeave() {
      this.factors = JSON.parse(this.originalFactorsStr).map(f => ({ ...f, _id: this.generateId() }));
      this.isDirty = false;
      this.$emit('dirty-state-changed', false);
      this.showUnsavedChangesPopup = false;

      if (this.pendingTab) {
        this.$emit('proceed-to-tab', this.pendingTab);
        this.pendingTab = null;
      }
      if (this.resolveNavigation) {
        this.resolveNavigation();
        this.resolveNavigation = null;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.criterion-item {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid transparent;

  &:hover {
    border-color: #e5e7eb;
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }
}

/* Modal styling matched to your design */
.popup-card {
  >>>.vs-popup--header { display: none !important; }
  >>>.vs-popup { width: 550px !important; }
}
.con-vs-popup>>>.vs-popup--content { padding: 0 !important; margin: 0 !important; overflow-x: hidden !important; width: auto !important; border-radius: 8px !important; background: white !important; }
.con-vs-popup>>>.vs-popup { background: white !important; border-radius: 8px !important; }
.vs-input>>>.vs-input--input { box-shadow: none; }
.vs-input>>>.vs-input--placeholder { top: 5px; }

/* Custom Add Button */
.custom-category-btn {
  border: 1px solid #f5bb00 !important;
  color: #f5bb00 !important;
  background: transparent !important;
  border-radius: 999px !important;
  padding: 8px 24px !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  box-shadow: none !important;
  transition: all 0.15s ease;
  
  &:hover { 
    background: #fff7cc !important; 
    transform: translateY(-1px);
  }
}

/* Transitions for List */
.list-enter-active, .list-leave-active {
  transition: all 0.4s ease;
}
.list-enter, .list-leave-to {
  opacity: 0;
  transform: translateY(15px);
}
/* Custom Popup Styling */
::v-deep .criteria-popup .vs-popup { 
  width: 550px !important; 
  border-radius: 8px !important;
}
::v-deep .criteria-popup .vs-popup--header {
  border-bottom: 1px solid #f3f4f6;
  background: white;
  border-radius: 8px 8px 0 0;
}
::v-deep .criteria-popup .vs-popup--title h3 {
  font-size: 1.15rem;
  font-weight: 600;
  color: #2c3e50;
}
::v-deep .criteria-popup .vs-popup--close {
  color: #6b7280;
  background: transparent !important;
}
::v-deep .criteria-popup .vs-popup--close:hover {
  color: #111827;
}
</style>